import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { waLink } from "@/lib/whatsapp";

const pad = (n) => String(n).padStart(2, "0");

// Deadline: sempre o próximo sábado às 12:00; ao passar, reinicia para o sábado seguinte
const getNextSaturdayNoon = () => {
    const now = new Date();
    const deadline = new Date(now);
    deadline.setHours(12, 0, 0, 0);
    let add = (6 - now.getDay() + 7) % 7;
    if (add === 0 && now.getTime() >= deadline.getTime()) add = 7;
    deadline.setDate(deadline.getDate() + add);
    return deadline.getTime();
};

const PromoCountdown = () => {
    const [deadline, setDeadline] = useState(getNextSaturdayNoon);
    const [now, setNow] = useState(() => Date.now());
    const [showFloat, setShowFloat] = useState(false);
    const bannerRef = useRef(null);

    useEffect(() => {
        const t = setInterval(() => {
            const ts = Date.now();
            setNow(ts);
            if (ts >= deadline) setDeadline(getNextSaturdayNoon());
        }, 1000);
        return () => clearInterval(t);
    }, [deadline]);

    useEffect(() => {
        const onScroll = () => {
            const el = bannerRef.current;
            if (el) setShowFloat(window.scrollY > el.offsetTop + el.offsetHeight);
        };
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const diff = Math.max(0, deadline - now);
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor(diff / 3600000) % 24;
    const minutes = Math.floor(diff / 60000) % 60;
    const seconds = Math.floor(diff / 1000) % 60;

    const units = [
        { v: days, l: "Dias", testid: "countdown-days" },
        { v: hours, l: "Horas", testid: "countdown-hours" },
        { v: minutes, l: "Min", testid: "countdown-minutes" },
        { v: seconds, l: "Seg", testid: "countdown-seconds" },
    ];

    return (
        <>
            <section ref={bannerRef} data-testid="promo-banner" className="bg-[#6A2022] text-white px-6 md:px-12 py-10 md:py-14">
                <div className="max-w-7xl mx-auto flex flex-col lg:flex-row lg:items-center justify-between gap-8 lg:gap-12">
                    <div className="max-w-md">
                        <p className="text-[10px] tracking-[0.45em] uppercase text-white/70 mb-3" data-testid="promo-label">
                            Condição especial
                        </p>
                        <h2 className="text-2xl md:text-4xl font-extrabold uppercase leading-[1.05] tracking-tight" data-testid="promo-headline">
                            Até 20% de desconto,{" "}
                            <span className="font-editorial italic font-light normal-case">
                                por tempo limitado.
                            </span>
                        </h2>
                    </div>

                    <div className="flex items-start gap-5 md:gap-8" data-testid="promo-countdown">
                        {units.map((u, i) => (
                            <div key={u.l} className="flex items-center gap-5 md:gap-8">
                                <div className="text-center">
                                    <span
                                        data-testid={u.testid}
                                        className="block font-editorial italic font-light text-4xl md:text-6xl leading-none tabular-nums"
                                    >
                                        {pad(u.v)}
                                    </span>
                                    <span className="block mt-2 text-[9px] tracking-[0.35em] uppercase text-white/60">
                                        {u.l}
                                    </span>
                                </div>
                                {i < units.length - 1 && (
                                    <span className="font-editorial italic text-3xl md:text-5xl text-white/30 -mt-4">:</span>
                                )}
                            </div>
                        ))}
                    </div>

                    <a
                        href={waLink("promo")}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="promo-cta-whatsapp"
                        className="inline-flex items-center justify-center gap-3 bg-white text-[#6A2022] px-8 py-4 text-[11px] tracking-[0.3em] uppercase font-bold transition-colors duration-300 hover:bg-[#0A0A0A] hover:text-white shrink-0"
                    >
                        Aproveitar pelo WhatsApp
                        <ArrowUpRight className="w-4 h-4" />
                    </a>
                </div>
            </section>

            <AnimatePresence>
                {showFloat && (
                    <motion.button
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 24 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        onClick={() => bannerRef.current?.scrollIntoView({ behavior: "smooth" })}
                        data-testid="promo-floating-countdown"
                        aria-label="Ver condição especial"
                        className="fixed z-50 bottom-20 left-4 md:bottom-8 md:left-8 flex items-center gap-3 bg-[#6A2022] text-white rounded-full pl-4 pr-6 py-4 shadow-2xl shadow-[#6A2022]/40 border border-white/25 wa-pulse"
                    >
                        <span className="relative flex w-2.5 h-2.5 shrink-0">
                            <span className="absolute inline-flex w-full h-full rounded-full bg-white animate-ping opacity-75" />
                            <span className="relative inline-flex w-2.5 h-2.5 rounded-full bg-white" />
                        </span>
                        <span className="text-[11px] tracking-[0.2em] uppercase font-extrabold whitespace-nowrap">
                            Até 20% OFF
                        </span>
                        <span className="text-[11px] tracking-[0.1em] uppercase text-white/85 whitespace-nowrap tabular-nums">
                            termina em {days > 0 ? `${days}d ` : ""}{pad(hours)}:{pad(minutes)}:{pad(seconds)}
                        </span>
                    </motion.button>
                )}
            </AnimatePresence>
        </>
    );
};

export default PromoCountdown;
